<template>
  <div
    class="kpi-grid"
    :class="'kpi-grid--cols-' + (cards.length || 3)"
    :style="{ '--kpi-count': cards.length || 3 }"
  >
    <div
      v-for="(card, index) in cards"
      :key="index"
      class="summary-card"
      :class="{ selected: card.key === activeKey }"
      role="button"
      tabindex="0"
      @click="$emit('select', card.key)"
      v-keyboard-activate :aria-pressed="String(card.key === activeKey)"
    >
      <div class="summary-card-top">
        <div class="summary-icon" :class="card.iconClass || 'icon-blue'">
          <CIcon :name="card.icon || 'cil-speedometer'" />
        </div>
        <span class="summary-more">•••</span>
      </div>

      <div class="summary-label" :title="card.title">
        {{ card.title }}
      </div>

      <div class="summary-bottom">
        <div class="summary-value">
          {{ card.value !== undefined && card.value !== null ? card.value : 0 }}
        </div>
        <span class="trend-badge" :class="card.badgeClass || 'trend-neutral'">
          {{ card.badge || 'KPI' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
export default {
  directives: { keyboardActivate },
  name: "HomeStatCards",
  props: {
    cards: {
      type: Array,
      default: () => [],
    },
    activeKey: {
      type: String,
      default: '',
    },
  },
};
</script>

<style scoped>
.kpi-grid {
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(var(--kpi-count, 3), minmax(0, 1fr));
  gap: 16px;
  margin: -18px 0 24px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-top: 0;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.035);
  box-sizing: border-box;
}

/* Explicit class-based column definitions */
.kpi-grid--cols-1 {
  grid-template-columns: minmax(0, 1fr);
}

.kpi-grid--cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.kpi-grid--cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.kpi-grid--cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.kpi-grid--cols-5 {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.kpi-grid--cols-6 {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.summary-card {
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 140px;
  height: auto;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #ffffff;
  border: 1px solid rgba(37, 99, 235, 0.10);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.summary-card.selected {
  border-color: #2563eb;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.16);
  background: #f0f7ff;
}

.summary-card-top {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.summary-icon {
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 16px;
}

.icon-blue {
  color: #2563eb;
  background: #eff6ff;
}

.icon-orange {
  color: #9a7b4f;
  background: #fdf8f3;
}

.icon-green {
  color: #2e7d32;
  background: #f1f8f2;
}

.icon-red {
  color: #d32f2f;
  background: #fdf2f2;
}

.icon-purple {
  color: #6f42c1;
  background: #f3eefa;
}

.icon-yellow {
  color: #fbc02d;
  background: #fefdf3;
}

.summary-more {
  color: #bbbbbb;
  font-size: 13px;
  line-height: 1;
  letter-spacing: 1px;
}

.summary-label {
  margin-top: 12px;
  overflow: hidden;
  color: #888888;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.summary-bottom {
  min-width: 0;
  margin-top: 6px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}

.summary-value {
  min-width: 0;
  color: #343a40;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
}

.trend-badge {
  flex: 0 0 auto;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.trend-up {
  color: #2e7d32;
  background: #f1f8f2;
}

.trend-down {
  color: #d32f2f;
  background: #fdf2f2;
}

.trend-neutral {
  color: #1e40af;
  background: #eff6ff;
}

.trend-orange {
  color: #9a7b4f;
  background: #fdf8f3;
}

.trend-purple {
  color: #6f42c1;
  background: #f3eefa;
}

.trend-yellow {
  color: #b8860b;
  background: #fefdf3;
}

/* Tablet & iPad */
@media (max-width: 992px) {
  .kpi-grid {
    gap: 12px;
    padding: 16px;
    margin: -18px 0 20px;
  }

  .kpi-grid--cols-4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .summary-card {
    padding: 14px 14px;
    min-height: 120px;
  }

  .summary-icon {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    font-size: 14px;
  }

  .summary-label {
    font-size: 10.5px;
    margin-top: 10px;
  }

  .summary-value {
    font-size: 24px;
  }

  .trend-badge {
    font-size: 7px;
    padding: 2px 6px;
  }
}

/* Mobile */
@media (max-width: 576px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 12px 8px;
    margin: -18px 0 16px;
  }

  .kpi-grid--cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 6px;
    padding: 10px 6px;
  }

  .kpi-grid--cols-3 .summary-card:last-child {
    grid-column: auto;
    justify-self: auto;
    width: 100%;
  }

  .summary-card {
    padding: 10px 6px;
    min-height: 96px;
  }

  .summary-icon {
    width: 26px;
    height: 26px;
    flex: 0 0 26px;
    font-size: 12px;
    border-radius: 6px;
  }

  .summary-more {
    font-size: 8px;
  }

  .summary-label {
    font-size: 9px;
    line-height: 1.25;
    margin-top: 6px;
    letter-spacing: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .summary-value {
    font-size: 18px;
    font-weight: 800;
  }

  .trend-badge {
    font-size: 6.5px;
    padding: 2px 4px;
    border-radius: 4px;
  }
}

/* Very small mobile */
@media (max-width: 360px) {
  .kpi-grid {
    gap: 4px;
    padding: 8px 4px;
  }

  .kpi-grid--cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 4px;
    padding: 8px 4px;
  }

  .kpi-grid--cols-3 .summary-card:last-child {
    grid-column: auto;
    width: 100%;
  }

  .summary-card {
    padding: 8px 4px;
    min-height: 88px;
  }

  .summary-label {
    font-size: 8px;
  }

  .summary-value {
    font-size: 15px;
  }

  .summary-more {
    display: none;
  }
}
</style>
